// Overture map layers and sources configuration
export const createOvertureSources = (fullApp) => {
  const sources = {};

  // Add Overture sources from app configuration if available
  if (fullApp?.overtureds && Array.isArray(fullApp.overtureds)) {
    fullApp.overtureds.forEach((overture) => {
      if (overture.url && overture.type) {
        sources[overture.type] = {
          type: "vector",
          url: `pmtiles://${overture.url}`
        };
      }
    });
  }

  return sources;
};

export const createOvertureLayers = (fullApp) => {
  const layers = [];

  // Add Overture layers from app configuration if available
  if (fullApp?.overtureds && Array.isArray(fullApp.overtureds)) {
    fullApp.overtureds.forEach((overture) => {
      if (overture.url && overture.type) {
        // Add building layers
        if (overture.type === "building") {
          layers.push({
            id: "building-3d",
            type: "fill-extrusion",
            source: overture.type,
            "source-layer": "building",
            minzoom: 15,
            layout: { visibility: "visible" },
            paint: {
              "fill-extrusion-color": "#ffaa00",
              "fill-extrusion-height": ["get", "height"],
              "fill-extrusion-base": ["get", "min_height"],
              "fill-extrusion-opacity": 0.9
            }
          });
          layers.push({
            id: "building-outline",
            type: "line",
            source: overture.type,
            "source-layer": "building",
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#333",
              "line-width": 0.8
            }
          });
        }

        // Add transportation layers with detailed styling
        if (overture.type === "transportation") {
          // Highway/road layers (major roads)
          layers.push({
            id: "transportation-highway",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["highway", "trunk", "primary", "secondary", "tertiary", "motorway"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#ffffff",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                10, 2,
                18, 8
              ],
              "line-opacity": 0.9
            }
          });

          // Highway/road casing (outline)
          layers.push({
            id: "transportation-highway-casing",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["highway", "trunk", "primary", "secondary", "tertiary", "motorway"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#333333",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                10, 3,
                18, 10
              ],
              "line-opacity": 0.8
            }
          });

          // Residential roads
          layers.push({
            id: "transportation-residential",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["residential", "service", "unclassified"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#ffffff",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                12, 1,
                18, 4
              ],
              "line-opacity": 0.7
            }
          });

          // Residential roads casing
          layers.push({
            id: "transportation-residential-casing",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["residential", "service", "unclassified"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#666666",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                12, 2,
                18, 6
              ],
              "line-opacity": 0.6
            }
          });

          // Pedestrian paths and sidewalks
          layers.push({
            id: "transportation-pedestrian",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["footway", "pedestrian", "path", "sidewalk"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#f8f8f8",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                14, 0.5,
                18, 2
              ],
              "line-opacity": 0.8,
              "line-dasharray": [2, 2]
            }
          });

          // Pedestrian paths casing
          layers.push({
            id: "transportation-pedestrian-casing",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["footway", "pedestrian", "path", "sidewalk"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#cccccc",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                14, 1,
                18, 3
              ],
              "line-opacity": 0.6
            }
          });

          // Cycle paths
          layers.push({
            id: "transportation-cycle",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["cycleway", "bridleway"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#4CAF50",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                12, 1,
                18, 3
              ],
              "line-opacity": 0.8,
              "line-dasharray": [4, 2]
            }
          });

          // Cycle paths casing
          layers.push({
            id: "transportation-cycle-casing",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["cycleway", "bridleway"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#2E7D32",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                12, 2,
                18, 5
              ],
              "line-opacity": 0.6
            }
          });

          // Railway tracks
          layers.push({
            id: "transportation-railway",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["rail"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#666666",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                10, 1,
                18, 3
              ],
              "line-opacity": 0.7,
              "line-dasharray": [1, 1]
            }
          });

          // Railway tracks casing
          layers.push({
            id: "transportation-railway-casing",
            type: "line",
            source: overture.type,
            "source-layer": "segment",
            filter: ["in", ["get", "class"], ["literal", ["rail"]]],
            layout: { visibility: "visible" },
            paint: {
              "line-color": "#333333",
              "line-width": [
                "interpolate",
                ["linear"],
                ["zoom"],
                10, 2,
                18, 5
              ],
              "line-opacity": 0.5
            }
          });
        }

        // Add places layers
        if (overture.type === "places") {
          layers.push({
            id: "places",
            type: "symbol",
            source: overture.type,
            "source-layer": "place",
            layout: {
              "text-field": ["get", "name"],
              "text-font": ["Noto Sans Regular"],
              "text-size": 12,
              "text-anchor": "center",
              "text-offset": [0, 0],
              "text-allow-overlap": false,
              "text-ignore-placement": false,
              "visibility": "visible"
            },
            paint: {
              "text-color": "#333333",
              "text-halo-color": "#ffffff",
              "text-halo-width": 1,
              "text-halo-blur": 0.5,
            },
          });
        }
      }
    });
  }

  return layers;
};

export const createOvertureLayerTree = (fullApp) => {
  const layerTree = {};

  // Add Overture layer configs from app configuration
  if (fullApp?.overtureds && Array.isArray(fullApp.overtureds)) {
    fullApp.overtureds.forEach((overture) => {
      if (overture.url && overture.type) {
        if (overture.type === "building") {
          if (!layerTree["overture-data"]) {
            layerTree["overture-data"] = {
              label: "City HD Map",
              layers: [],
              children: {}
            };
          }
          layerTree["overture-data"].children["building"] = {
            label: "Buildings (3D)",
            layers: ["building-3d", "building-outline"]
          };
          layerTree["overture-data"].layers.push("building-3d", "building-outline");
        } else if (overture.type === "transportation") {
          if (!layerTree["overture-data"]) {
            layerTree["overture-data"] = {
              label: "Overture Data",
              layers: [],
              children: {}
            };
          }

          const transportationLayers = [
            "transportation-highway-casing", "transportation-highway",
            "transportation-residential-casing", "transportation-residential",
            "transportation-pedestrian-casing", "transportation-pedestrian",
            "transportation-cycle-casing", "transportation-cycle",
            "transportation-railway-casing", "transportation-railway"
          ];

          layerTree["overture-data"].children["transportation"] = {
            label: "Transportation",
            layers: transportationLayers,
            children: {
              "transportation-highway": {
                label: "Highways & Major Roads",
                layers: ["transportation-highway-casing", "transportation-highway"]
              },
              "transportation-residential": {
                label: "Residential Roads",
                layers: ["transportation-residential-casing", "transportation-residential"]
              },
              "transportation-pedestrian": {
                label: "Pedestrian Paths",
                layers: ["transportation-pedestrian-casing", "transportation-pedestrian"]
              },
              "transportation-cycle": {
                label: "Cycle Paths",
                layers: ["transportation-cycle-casing", "transportation-cycle"]
              },
              "transportation-railway": {
                label: "Railways",
                layers: ["transportation-railway-casing", "transportation-railway"]
              }
            }
          };
          layerTree["overture-data"].layers.push(...transportationLayers);
        } else if (overture.type === "places") {
          if (!layerTree["overture-data"]) {
            layerTree["overture-data"] = {
              label: "Overture Data",
              layers: [],
              children: {}
            };
          }
          layerTree["overture-data"].children["places"] = {
            label: "Places",
            layers: ["places"]
          };
          layerTree["overture-data"].layers.push("places");
        }
      }
    });
  }

  return layerTree;
};

export const createOvertureLayerVisibility = (fullApp) => {
  const layerVisibility = {};

  // Add Overture layer visibility from app configuration
  if (fullApp?.overtureds && Array.isArray(fullApp.overtureds)) {
    fullApp.overtureds.forEach((overture) => {
      if (overture.url && overture.type) {
        if (overture.type === "building") {
          layerVisibility["building-3d"] = true;
          layerVisibility["building-outline"] = true;
        } else if (overture.type === "transportation") {
          layerVisibility["transportation-highway"] = true;
          layerVisibility["transportation-residential"] = true;
          layerVisibility["transportation-pedestrian"] = true;
          layerVisibility["transportation-cycle"] = true;
          layerVisibility["transportation-railway"] = true;
        } else if (overture.type === "places") {
          layerVisibility["places"] = true;
        }
      }
    });
  }

  return layerVisibility;
}; 